import React, { useState } from 'react';
import { addDoc, collection } from 'firebase/firestore';
import { db } from '../firebase';
import { Upload, FileSpreadsheet, AlertCircle, CheckCircle } from 'lucide-react';

const ExcelImport = ({ onClose, onSuccess }) => {
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      if (selectedFile.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' || 
          selectedFile.type === 'application/vnd.ms-excel') {
        setFile(selectedFile);
        setError('');
        setSuccess('');
        // Preview the file
        previewExcelFile(selectedFile);
      } else {
        setError('Please select a valid Excel file (.xlsx or .xls)');
        setFile(null);
      }
    }
  };

  const previewExcelFile = async (file) => {
    try {
      // Dynamic import of xlsx library
      const XLSX = await import('xlsx');
      const reader = new FileReader();
      
      reader.onload = (e) => {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
        
        // Process preview data with proper date formatting
        const processedPreview = jsonData.slice(0, 5).map(row => {
          if (row.length > 0) {
            // Format the date column (first column)
            const formattedRow = [...row];
            if (formattedRow[0]) {
              formattedRow[0] = formatDate(formattedRow[0]);
            }
            return formattedRow;
          }
          return row;
        });
        
        setPreview(processedPreview);
      };
      
      reader.readAsArrayBuffer(file);
    } catch (error) {
      console.error('Error previewing file:', error);
      setError('Error reading Excel file. Please make sure it\'s a valid Excel file.');
    }
  };

  const processExcelData = async (file) => {
    try {
      const XLSX = await import('xlsx');
      const reader = new FileReader();
      
      return new Promise((resolve, reject) => {
        reader.onload = (e) => {
          try {
            const data = new Uint8Array(e.target.result);
            const workbook = XLSX.read(data, { type: 'array' });
            const sheetName = workbook.SheetNames[0];
            const worksheet = workbook.Sheets[sheetName];
            const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
            
            // Process the data according to your column structure
            const processedData = processDataRows(jsonData);
            resolve(processedData);
          } catch (error) {
            reject(error);
          }
        };
        
        reader.onerror = () => reject(new Error('Error reading file'));
        reader.readAsArrayBuffer(file);
      });
    } catch (error) {
      throw new Error('Error processing Excel file');
    }
  };

  const processDataRows = (rows) => {
    return rows.map((row, index) => {
      const data = {
        farmerName: row[0] || '', // Column A - Farmer Name
        date: formatDate(row[1]), // Column B - Date
        delivered: parseFloat(row[2]) || 0, // Column C - Delivered
        trayJama: parseFloat(row[3]) || 0, // Column D - Tray Jama
        eggTray: parseFloat(row[4]) || 0, // Column E - Egg Tray
        ogRate: parseFloat(row[5]) || 0, // Column F - OG Rate
        rate: parseFloat(row[6]) || 0, // Column G - Rate
        totalPrice: parseFloat(row[7]) || 0, // Column H - Total Price
        paymentJama: parseFloat(row[8]) || 0, // Column I - Payment Jama
        paymentReceived: parseFloat(row[9]) || 0, // Column J - Payment Received
        discount: 0, // Default discount
        createdAt: new Date(),
        updatedAt: new Date()
      };
      
      // Validate required fields
      if (!data.farmerName || !data.date) {
        throw new Error(`Row ${index + 2}: Farmer Name and Date are required`);
      }
      
      return data;
    });
  };

  const formatDate = (dateValue) => {
    if (!dateValue) return '';
    
    try {
      // Handle different date formats
      if (dateValue instanceof Date) {
        return dateValue.toISOString().split('T')[0];
      }
      
      if (typeof dateValue === 'string') {
        // Try to parse date string
        const date = new Date(dateValue);
        if (!isNaN(date.getTime())) {
          return date.toISOString().split('T')[0];
        }
      }
      
      if (typeof dateValue === 'number') {
        // Excel date number
        const date = new Date((dateValue - 25569) * 86400 * 1000);
        return date.toISOString().split('T')[0];
      }
      
      return '';
    } catch (error) {
      console.error('Error formatting date:', error);
      return '';
    }
  };

  const handleImport = async () => {
    if (!file) {
      setError('Please select a file first');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const data = await processExcelData(file);
      
      if (data.length === 0) {
        setError('No valid data found in the Excel file');
        return;
      }

      // Import data to Firestore
      const batch = [];
      for (const record of data) {
        try {
          await addDoc(collection(db, 'sales'), record);
          batch.push(record);
        } catch (error) {
          console.error('Error adding record:', error);
        }
      }

      setSuccess(`Successfully imported ${batch.length} records`);
      onSuccess();
      
      // Clear form after successful import
      setTimeout(() => {
        onClose();
      }, 2000);

    } catch (error) {
      console.error('Import error:', error);
      setError('Error importing data. Please check your Excel file format.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-gray-800">Import Excel Data</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            ✕
          </button>
        </div>

        <div className="space-y-6">
          {/* File Upload */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select Excel File
            </label>
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
              <input
                type="file"
                accept=".xlsx,.xls"
                onChange={handleFileChange}
                className="hidden"
                id="excel-file"
              />
              <label htmlFor="excel-file" className="cursor-pointer">
                <FileSpreadsheet className="mx-auto h-12 w-12 text-gray-400" />
                <p className="mt-2 text-sm text-gray-600">
                  Click to select or drag and drop
                </p>
                <p className="text-xs text-gray-500">
                  Supports .xlsx and .xls files
                </p>
              </label>
            </div>
            {file && (
              <p className="mt-2 text-sm text-gray-600">
                Selected: {file.name}
              </p>
            )}
          </div>

          {/* Error/Success Messages */}
          {error && (
            <div className="flex items-center p-4 bg-red-50 border border-red-200 rounded-md">
              <AlertCircle className="h-5 w-5 text-red-400 mr-2" />
              <span className="text-red-700">{error}</span>
            </div>
          )}

          {success && (
            <div className="flex items-center p-4 bg-green-50 border border-green-200 rounded-md">
              <CheckCircle className="h-5 w-5 text-green-400 mr-2" />
              <span className="text-green-700">{success}</span>
            </div>
          )}

          {/* Preview */}
          {preview && (
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-3">Preview (First 5 rows)</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full border border-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Farmer Name</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Delivered</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Tray Jama</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Egg Tray</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">OG Rate</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Rate</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Total Price</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Payment Jama</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Payment Received</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {preview.map((row, index) => (
                      <tr key={index}>
                        {row.slice(0, 10).map((cell, cellIndex) => (
                          <td key={cellIndex} className="px-3 py-2 text-sm text-gray-900">
                            {cell || '-'}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Instructions */}
          <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
            <h4 className="text-sm font-medium text-blue-900 mb-2">Expected Excel Format:</h4>
            <p className="text-sm text-blue-700 mb-2">
              Your Excel file should have these columns in order:
            </p>
            <div className="text-xs text-blue-600 space-y-1">
              <p>A: Farmer Name | B: Date | C: Delivered | D: Tray Jama | E: Egg Tray | F: OG Rate</p>
              <p>G: Rate | H: Total Price | I: Payment Jama | J: Payment Received</p>
            </div>
            <p className="text-sm text-blue-700 mt-2">
              The first row should contain headers. Data will be imported starting from row 2.
            </p>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300"
            >
              Cancel
            </button>
            <button
              onClick={handleImport}
              disabled={!file || loading}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 flex items-center"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Importing...
                </>
              ) : (
                <>
                  <Upload className="h-4 w-4 mr-2" />
                  Import Data
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExcelImport; 